import React, { useState, useMemo } from 'react';
import { X, DollarSign, Target, TrendingUp, Users, AlertCircle, Activity, Calendar, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Link } from 'react-router-dom';

const SimulationDashboard = ({ industry = 'retail', revenue = '10-50m', onClose }) => {
  // State for interactive controls
  const [currentPrice, setCurrentPrice] = useState(100);
  const [targetPrice, setTargetPrice] = useState(120);
  const [volume, setVolume] = useState(1000);
  const [marketCondition, setMarketCondition] = useState('normal');

  // Industry-specific elasticity coefficients
  const elasticityMap = {
    retail: -1.2,
    healthcare: -0.5,
    saas: -0.8,
    manufacturing: -0.9,
    services: -1.0,
    trading: -1.5
  };

  // Market condition multipliers
  const marketMultipliers = {
    high_competition: 0.85,
    normal: 1.0,
    low_competition: 1.15,
    seasonal_high: 1.25
  };

  const elasticity = elasticityMap[industry] || -1.0;
  const marketMultiplier = marketMultipliers[marketCondition] || 1.0;

  // Core calculations (memoized for performance)
  const calculations = useMemo(() => {
    const priceChange = ((targetPrice - currentPrice) / currentPrice) * 100;
    const demandChange = elasticity * priceChange;
    const newVolume = volume * (1 + demandChange / 100);
    const currentRevenue = currentPrice * volume;
    const projectedRevenue = targetPrice * newVolume * marketMultiplier;
    const revenueImpact = ((projectedRevenue - currentRevenue) / currentRevenue) * 100;

    // Margin calculation
    const baseMargin = 35;
    const marginImpact = (targetPrice - currentPrice) / currentPrice * 10;
    const projectedMargin = Math.max(0, Math.min(100, baseMargin + marginImpact));

    // Competitive score
    const competitiveScore = Math.max(0, Math.min(100, (100 - Math.abs(priceChange) * 0.5) * marketMultiplier));

    // Retention impact
    const retentionImpact = priceChange > 0 
      ? Math.max(-15, -priceChange * 0.5)
      : Math.min(5, Math.abs(priceChange) * 0.3);

    return {
      priceChange,
      demandChange,
      newVolume,
      currentRevenue,
      projectedRevenue,
      revenueImpact,
      projectedMargin,
      competitiveScore,
      retentionImpact
    };
  }, [currentPrice, targetPrice, volume, elasticity, marketMultiplier]);

  // AI Insights generation - Strategic recommendations
  const insights = useMemo(() => {
    const priceChange = ((targetPrice - currentPrice) / currentPrice) * 100;
    const isPriceIncrease = priceChange > 0;
    const optimalPrice = currentPrice * (1 + (-elasticity / 2) / 100);
    const isNearOptimal = Math.abs(targetPrice - optimalPrice) / optimalPrice < 0.1;
    
    const strategicInsights = [];
    
    // Insight 1: Price Strategy Recommendation
    if (isPriceIncrease && calculations.demandChange < -15) {
      strategicInsights.push(`⚠️ Warning: ${Math.abs(priceChange).toFixed(1)}% price increase may trigger significant demand loss. Consider a gradual rollout or value-add bundles to justify the premium.`);
    } else if (isPriceIncrease && calculations.revenueImpact > 0) {
      strategicInsights.push(`✓ Strategic Opportunity: Your target price increase captures higher margins while maintaining acceptable demand levels. This is a strong value-based pricing position for ${industry}.`);
    } else if (!isPriceIncrease && calculations.demandChange > 10) {
      strategicInsights.push(`✓ Growth Strategy: Lower pricing drives significant volume increase. Best for market share expansion, but monitor margin sustainability and ensure operational capacity can handle increased demand.`);
    } else if (isNearOptimal) {
      strategicInsights.push(`✓ Optimal Positioning: Your target price is within 10% of the AI-recommended optimal point of $${Math.round(optimalPrice)}. This balances revenue maximization with market dynamics.`);
    }
    
    // Insight 2: Competitive Positioning
    if (calculations.competitiveScore > 75) {
      strategicInsights.push(`💡 Competitive Advantage: Strong market position enables premium pricing. Leverage this with differentiated messaging and consider value-based pricing tiers rather than competing on cost.`);
    } else if (calculations.competitiveScore < 50) {
      strategicInsights.push(`⚠️ Competitive Pressure: Below-market positioning detected. Either enhance product differentiation or accept lower margins. Consider targeted competitor analysis to identify pricing gaps.`);
    } else {
      strategicInsights.push(`💡 Market Alignment: Your pricing sits in the competitive sweet spot. Focus on service quality and customer experience to justify your position and prevent race-to-bottom dynamics.`);
    }
    
    // Insight 3: Customer Retention Strategy
    if (calculations.retentionImpact < -8) {
      strategicInsights.push(`🚨 Retention Risk: Significant churn threat detected. Implement customer communication plan, grandfather existing customers, or add retention incentives before price change goes live.`);
    } else if (calculations.retentionImpact > 5) {
      strategicInsights.push(`✓ Customer Win: Improved retention expected. Your pricing change signals better value. Capitalize by highlighting ROI improvements in customer communications.`);
    } else {
      strategicInsights.push(`💡 Retention Stable: Minimal customer churn expected. This is a low-risk pricing adjustment. Still recommend proactive customer communication to maintain trust.`);
    }
    
    // Insight 4: Execution Recommendation
    if (Math.abs(priceChange) > 15) {
      strategicInsights.push(`📋 Implementation: Large price change requires phased rollout. Test with 20% of customers first, gather feedback, then expand. Prepare customer success team with FAQs and retention offers.`);
    } else if (calculations.projectedMargin > 45) {
      strategicInsights.push(`📋 Next Steps: Strong margins enable reinvestment. Allocate gains toward product enhancement, customer acquisition, or market expansion to compound growth momentum.`);
    } else {
      strategicInsights.push(`📋 Action Plan: Price change is ready for implementation. Brief sales team on value messaging, update website/contracts, and monitor first 30 days closely for market signals.`);
    }
    
    return strategicInsights;
  }, [currentPrice, targetPrice, elasticity, industry, calculations]);

  const metrics = [
    {
      icon: DollarSign,
      label: 'Revenue Impact',
      value: `${calculations.revenueImpact > 0 ? '+' : ''}${calculations.revenueImpact.toFixed(1)}%`,
      positive: calculations.revenueImpact >= 0,
      description: 'Projected percentage change in total revenue based on the target price adjustment. Positive values indicate revenue growth, while negative values suggest potential revenue decline.'
    },
    {
      icon: Target,
      label: 'Profit Margin',
      value: `${calculations.projectedMargin.toFixed(1)}%`,
      positive: calculations.projectedMargin > 35,
      description: 'Expected profit margin after implementing the target price. This represents the percentage of revenue retained as profit after covering all costs. Higher margins indicate better profitability.'
    },
    {
      icon: TrendingUp,
      label: 'Demand Change',
      value: `${calculations.demandChange > 0 ? '+' : ''}${calculations.demandChange.toFixed(1)}%`,
      positive: calculations.demandChange >= -5,
      description: 'Estimated change in customer demand based on price elasticity. Negative values indicate reduced demand due to price increases, while positive values suggest increased demand from price reductions.'
    },
    {
      icon: Activity,
      label: 'Competitive Score',
      value: `${Math.round(calculations.competitiveScore)}/100`,
      positive: calculations.competitiveScore > 60,
      description: 'Market competitiveness rating comparing your target price to industry benchmarks. Scores above 60 indicate strong competitive positioning, while lower scores suggest potential market challenges.'
    },
    {
      icon: Users,
      label: 'Retention Impact',
      value: `${calculations.retentionImpact > 0 ? '+' : ''}${calculations.retentionImpact.toFixed(1)}%`,
      positive: calculations.retentionImpact >= 0,
      description: 'Projected impact on customer retention rates. Positive values indicate improved customer loyalty, while negative values suggest potential increased churn due to pricing changes.'
    }
  ];

  return (
    <div className="fixed inset-0 bg-[#0A0F1C] z-50 overflow-y-auto">
      <div className="max-w-[1400px] mx-auto p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-4xl font-bold text-[#F8FAFC] mb-2">Adaptive Pricing Simulation</h2>
            <div className="flex items-center gap-4">
              <span className="px-3 py-1 bg-[#2563EB]/20 text-[#2563EB] rounded text-sm font-semibold uppercase">
                {industry}
              </span>
              <span className="text-[#94A3B8]">Revenue: {revenue}</span>
            </div>
          </div>
          <Link to="/pricing-check">
            <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-6 py-3 rounded-lg font-semibold">
              <ArrowRight className="w-5 h-5 mr-2" />
              Run Pricing Check
            </Button>
          </Link>
        </div>

        {/* AI Insights - Moved to Top */}
        <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-[#2563EB] to-[#3B82F6] rounded-xl flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-[#F8FAFC]">AI Powered Insights</h3>
          </div>
          <ul className="space-y-4">
            {insights.map((insight, index) => (
              <li key={index} className="flex items-start gap-4 text-[#94A3B8] text-lg">
                <span className="text-[#2563EB] mt-1 text-xl">•</span>
                <span>{insight}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          {metrics.map((metric, index) => (
            <div 
              key={index} 
              className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6 hover:border-[#2563EB]/60 transition-all relative group"
            >
              <div className="flex items-center gap-3 mb-3">
                <metric.icon className="w-5 h-5 text-[#2563EB]" />
                <span className="text-[#94A3B8] text-sm">{metric.label}</span>
                <HelpCircle className="w-4 h-4 text-[#94A3B8]/50 ml-auto cursor-help" />
              </div>
              <div className={`text-3xl font-bold ${metric.positive ? 'text-[#2563EB]' : 'text-red-400'}`}>
                {metric.value}
              </div>
              
              {/* Tooltip on hover */}
              <div className="absolute left-0 right-0 top-full mt-2 p-4 bg-[#1E293B] border border-[#2563EB]/50 rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 text-sm text-[#E2E8F0] leading-relaxed">
                {metric.description}
              </div>
            </div>
          ))}
        </div>

        {/* Interactive Controls */}
        <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-8 mb-8">
          <h3 className="text-2xl font-bold text-[#F8FAFC] mb-6">Simulation Controls</h3>
          <div className="grid grid-cols-4 gap-8">
            {/* Current Price Slider */}
            <div>
              <Label className="text-[#F8FAFC] mb-3 block text-base font-semibold">Current Price: ${currentPrice}</Label>
              <input
                type="range"
                min="50"
                max="500"
                step="5"
                value={currentPrice}
                onChange={(e) => setCurrentPrice(Number(e.target.value))}
                className="w-full h-3 bg-[#1E293B] rounded-lg appearance-none cursor-pointer slider-thumb"
                style={{
                  background: `linear-gradient(to right, #2563EB 0%, #2563EB ${((currentPrice - 50) / (500 - 50)) * 100}%, #1E293B ${((currentPrice - 50) / (500 - 50)) * 100}%, #1E293B 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-[#94A3B8] mt-2">
                <span>$50</span>
                <span>$500</span>
              </div>
            </div>

            {/* Target Price Slider */}
            <div>
              <Label className="text-[#F8FAFC] mb-3 block text-base font-semibold">Target Price: ${targetPrice}</Label>
              <input
                type="range"
                min="50"
                max="500"
                step="5"
                value={targetPrice}
                onChange={(e) => setTargetPrice(Number(e.target.value))}
                className="w-full h-3 bg-[#1E293B] rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #2563EB 0%, #2563EB ${((targetPrice - 50) / (500 - 50)) * 100}%, #1E293B ${((targetPrice - 50) / (500 - 50)) * 100}%, #1E293B 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-[#94A3B8] mt-2">
                <span>$50</span>
                <span>$500</span>
              </div>
            </div>

            {/* Volume Slider */}
            <div>
              <Label className="text-[#F8FAFC] mb-3 block text-base font-semibold">Volume: {volume} units</Label>
              <input
                type="range"
                min="100"
                max="10000"
                step="100"
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="w-full h-3 bg-[#1E293B] rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #2563EB 0%, #2563EB ${((volume - 100) / (10000 - 100)) * 100}%, #1E293B ${((volume - 100) / (10000 - 100)) * 100}%, #1E293B 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-[#94A3B8] mt-2">
                <span>100</span>
                <span>10K</span>
              </div>
            </div>

            {/* Market Conditions Dropdown */}
            <div>
              <Label className="text-[#F8FAFC] mb-3 block text-base font-semibold">Market Conditions</Label>
              <select
                value={marketCondition}
                onChange={(e) => setMarketCondition(e.target.value)}
                className="w-full bg-[#1E293B] text-[#F8FAFC] border border-[#2563EB]/30 rounded-lg px-4 py-3 focus:outline-none focus:border-[#2563EB] transition-colors"
              >
                <option value="high_competition">High Competition (0.85x)</option>
                <option value="normal">Normal (1.0x)</option>
                <option value="low_competition">Low Competition (1.15x)</option>
                <option value="seasonal_high">Seasonal High (1.25x)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-8">
          <h3 className="text-xl font-bold text-[#F8FAFC] mb-4">Simulation Summary</h3>
          <div className="grid grid-cols-3 gap-6 text-[#94A3B8]">
            <div>
              <div className="text-sm mb-1">Current Revenue</div>
              <div className="text-2xl font-bold text-[#F8FAFC]">${(calculations.currentRevenue / 1000).toFixed(1)}K</div>
            </div>
            <div>
              <div className="text-sm mb-1">Projected Revenue</div>
              <div className="text-2xl font-bold text-[#2563EB]">${(calculations.projectedRevenue / 1000).toFixed(1)}K</div>
            </div>
            <div>
              <div className="text-sm mb-1">Volume Change</div>
              <div className="text-2xl font-bold text-[#F8FAFC]">{Math.round(calculations.newVolume)} units</div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #2563EB;
          cursor: pointer;
          border: 3px solid #F8FAFC;
          box-shadow: 0 0 10px rgba(37, 99, 235, 0.5);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #2563EB;
          cursor: pointer;
          border: 3px solid #F8FAFC;
          box-shadow: 0 0 10px rgba(37, 99, 235, 0.5);
        }
      `}</style>
    </div>
  );
};

export default SimulationDashboard;
